import { useEffect, useState } from "react";
import { Layers } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import {
  BUYER_MODULE_VISIBILITY_LABELS,
  BUYER_VISIBILITY_MODULE_IDS,
  normalizeBuyerModuleVisibility,
} from "@/lib/buyerModuleVisibility";
import {
  updateBuyerModuleVisibility,
  useBuyerModuleVisibility,
} from "@/lib/useBuyerModuleVisibility";

/**
 * Solo diseñador BNT — define qué módulos ve el comprador (ocultar los en construcción).
 */
export default function BuyerModuleVisibilityPanel() {
  const { modules, loading } = useBuyerModuleVisibility();
  const [draft, setDraft] = useState(modules);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(modules);
  }, [modules]);

  const onToggle = async (moduleId, checked) => {
    const next = normalizeBuyerModuleVisibility({ ...draft, [moduleId]: checked });
    setDraft(next);
    setSaving(true);
    try {
      await updateBuyerModuleVisibility(next);
      toast.success(
        checked
          ? `${BUYER_MODULE_VISIBILITY_LABELS[moduleId]} visible para compradores.`
          : `${BUYER_MODULE_VISIBILITY_LABELS[moduleId]} oculto (en construcción).`,
      );
    } catch (e) {
      setDraft(modules);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la visibilidad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-md border border-slate-200 bg-white shadow-sm p-4 space-y-4"
      data-testid="buyer-module-visibility-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <Layers className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#0F2D5C]">
            Módulos visibles para compradores
          </Label>
          <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
            Oculte módulos en construcción; el comprador no verá su tarjeta ni podrá entrar por URL.
            Use la vista de prueba como comprador para validar el resultado.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {BUYER_VISIBILITY_MODULE_IDS.map((moduleId) => (
          <div
            key={moduleId}
            className="flex items-center justify-between gap-3 rounded-sm border border-slate-100 px-3 py-2.5"
            data-testid={`buyer-module-visibility-row-${moduleId}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0F2D5C]">
                {BUYER_MODULE_VISIBILITY_LABELS[moduleId]}
              </p>
              <p className="text-[11px] text-slate-500">
                {draft[moduleId] ? "Visible en Panel General" : "Oculto — en construcción"}
              </p>
            </div>
            <Switch
              checked={Boolean(draft[moduleId])}
              disabled={loading || saving}
              onCheckedChange={(checked) => onToggle(moduleId, checked)}
              data-testid={`buyer-module-visibility-switch-${moduleId}`}
              aria-label={BUYER_MODULE_VISIBILITY_LABELS[moduleId]}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
